/**
 * VAT Toggle functionality
 * Handles dynamic price updates when VAT toggle state changes
 */

class VatToggle {
  constructor() {
    this.settings = window.VatSettings || { rate: 20, pricesIncludeVat: false, currency: 'GBP' };
    this.translations = window.VatToggleTranslations || { inclusive: 'Inc. VAT', exclusive: 'Ex. VAT' };
    this.vatRate = this.settings.rate / 100;
    this.pricesIncludeVat = this.settings.pricesIncludeVat;
    this.init();
  }

  init() {
    // Set initial state from cookie
    const vatState = this.getCookie('vat_toggle_state') || 'exclusive';
    this.updateVatToggleState(vatState);
    this.updateAllPrices(vatState);

    // Listen for VAT toggle changes
    document.addEventListener('vatToggleChanged', (event) => {
      this.updateAllPrices(event.detail.state);
    });

    // Listen for page changes (for SPA-like behavior)
    document.addEventListener('DOMContentLoaded', () => {
      const vatState = this.getCookie('vat_toggle_state') || 'exclusive';
      this.updateAllPrices(vatState);
    });

    // Listen for collection rerendered events (e.g., when load more is used)
    document.addEventListener('collection:rerendered', () => {
      const vatState = this.getCookie('vat_toggle_state') || 'exclusive';
      this.updateAllPrices(vatState);
    });

    // Listen for product recommendations loaded events (e.g., "You May Also Like" section)
    document.addEventListener('recommendations:loaded', () => {
      const vatState = this.getCookie('vat_toggle_state') || 'exclusive';
      this.updateAllPrices(vatState);
    });
  }

  updateVatToggleState(state) {
    const vatToggle = document.querySelector('[data-vat-toggle]');
    if (!vatToggle) return;

    const vatToggleSwitch = vatToggle.querySelector('[data-vat-toggle-switch]');
    const vatToggleLabel = vatToggle.querySelector('.vat-toggle__label');

    if (vatToggleSwitch) {
      vatToggleSwitch.setAttribute('data-vat-state', state);
    }

    if (vatToggleLabel) {
      if (state === 'inclusive') {
        vatToggleLabel.textContent = this.translations.inclusive;
        vatToggleLabel.className = 'vat-toggle__label vat-toggle__label--inclusive';
      } else {
        vatToggleLabel.textContent = this.translations.exclusive;
        vatToggleLabel.className = 'vat-toggle__label vat-toggle__label--exclusive';
      }
    }
  }

  updateAllPrices(vatState) {
    this.updateProductPrices(vatState);
    this.updateCartPrices(vatState);
  }

  updateProductPrices(vatState) {
    // Find all price elements
    const priceElements = document.querySelectorAll('.f-price-item, .price, .product-price, .f-price-b2b--available');

    priceElements.forEach(priceElement => {
      this.updatePriceElement(priceElement, vatState);
    });
  }

  updatePriceElement(priceElement, vatState) {
    const originalText = priceElement.getAttribute('data-original-price') || priceElement.textContent.trim();

    // Store original price if not already stored
    if (!priceElement.getAttribute('data-original-price')) {
      priceElement.setAttribute('data-original-price', originalText);
    }

    // Extract numeric price value
    const priceMatch = originalText.match(/[\d,]+\.?\d*/);
    if (!priceMatch) return;

    const numericPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
    const currencySymbol = originalText.replace(/[\d,]+\.?\d*/, '').trim();

    let calculatedPrice;
    let vatIndicator;

    if (vatState === 'inclusive') {
      if (this.pricesIncludeVat) {
        // Prices already include VAT, show as inclusive
        calculatedPrice = numericPrice;
        vatIndicator = `(${this.translations.inclusive})`;
      } else {
        // Prices are exclusive, calculate inclusive price
        calculatedPrice = numericPrice * (1 + this.vatRate);
        vatIndicator = `(${this.translations.inclusive})`;
      }
    } else {
      if (this.pricesIncludeVat) {
        // Prices include VAT, calculate exclusive price
        calculatedPrice = numericPrice / (1 + this.vatRate);
        vatIndicator = `(${this.translations.exclusive})`;
      } else {
        // Prices already exclusive, show as exclusive
        calculatedPrice = numericPrice;
        vatIndicator = `(${this.translations.exclusive})`;
      }
    }

    // Format the price
    const formattedPrice = this.formatPrice(calculatedPrice, currencySymbol);

    // Update price display
    priceElement.textContent = formattedPrice;

    // Update or add VAT indicator
    let vatIndicatorElement = priceElement.parentElement.querySelector('.f-price__vat-indicator');
    if (!vatIndicatorElement) {
      vatIndicatorElement = document.createElement('span');
      vatIndicatorElement.className = 'f-price__vat-indicator font-body-regular text-base';
      const deliveryEl = priceElement.parentElement.querySelector('.free-delivery-text')
      if(deliveryEl){
        priceElement.parentElement.insertBefore(vatIndicatorElement, deliveryEl);
      } else {
        priceElement.parentElement.appendChild(vatIndicatorElement);
      }
    }
    vatIndicatorElement.textContent = vatIndicator;
  }

  updateCartPrices(vatState) {
    const cartPrices = document.querySelectorAll('.cart-price, .cart-item__price, .cart-drawer__price, .cart-total');

    cartPrices.forEach(priceElement => {
      this.updatePriceElement(priceElement, vatState);
    });
  }

  formatPrice(price, currencySymbol) {
    // Simple price formatting - can be enhanced for different currencies
    const formatted = price.toFixed(2);
    return `${currencySymbol}${formatted}`;
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  setCookie(name, value, days = 28) {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + days);
    document.cookie = `${name}=${value}; expires=${expiryDate.toUTCString()}; path=/`;
  }
}

// Initialize VAT toggle functionality when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new VatToggle();
});

// Also initialize if the script is loaded after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VatToggle();
  });
} else {
  new VatToggle();
}
